import React from 'react';
import type { BundleProps } from 'signer-app/bundle-props';
import { useLatestRef } from 'signer-app/utils/use-latest-ref';

export enum MessageType {
  // Use of literal "hellosign" here is acceptable. Questions? #ask-hs-frontend.
  /* eslint-disable no-restricted-syntax */
  APP_INITIALIZE = 'hellosign:initialize',
  USER_DECLINE_REQUEST = 'hellosign:userDeclineRequest',
  USER_FINISH_REQUEST = 'hellosign:userFinishRequest',
  USER_SIGN_REQUEST = 'hellosign:userSignRequest',
  USER_REASSIGN_REQUEST = 'hellosign:userReassignRequest',
  USER_CANCEL_REQUEST = 'hellosign:userCancelRequest',
  APP_CONFIGURE = 'hellosign:configure',
  APP_ERROR = 'hellosign:error',
}

type ReassignRequest = {
  type: MessageType.USER_REASSIGN_REQUEST;
  payload: {
    name: string;
    email: string;
    reason: string;
    signatureId: string;
  };
};

type AppError = {
  type: MessageType.APP_ERROR;
  payload: {
    signatureId: string;
    code: string;
  };
};

export type EmbeddedErrorCode =
  | 'bad_method'
  | 'bad_request'
  | 'credentials_required'
  | 'network_error'
  | 'not_found'
  | 'server_error'
  | 'unknown';

export function getEmbeddedErrorCode(error: unknown): EmbeddedErrorCode {
  if ((error as Error)?.name === 'FetchError') {
    return 'network_error';
  }

  const response = (error as { response?: Response })?.response;
  if (!response) {
    return 'unknown';
  }

  if (response.status >= 500) {
    return 'server_error';
  }

  switch (response.status) {
    case 400:
      return 'bad_request';
    case 401:
      return 'credentials_required';
    case 404:
    case 410:
      return 'not_found';
    case 405:
      return 'bad_method';
    default:
      return 'unknown';
  }
}

const sentEmbeddedErrors = new Set<string>();

export const canPostMessage = (
  embeddedData: BundleProps['embeddedData'],
): embeddedData is NonNullable<BundleProps['embeddedData']> =>
  !!embeddedData?.isEmbeddedSigning &&
  !!embeddedData.parentUrl &&
  !!embeddedData.signatureId;

export function sendEmbeddedError(
  embeddedData: BundleProps['embeddedData'],
  code: EmbeddedErrorCode,
) {
  if (!canPostMessage(embeddedData)) {
    return;
  }

  const key = `${embeddedData.parentUrl}:${embeddedData.signatureId}`;
  if (sentEmbeddedErrors.has(key)) {
    return;
  }
  sentEmbeddedErrors.add(key);

  postMessage(
    {
      type: MessageType.APP_ERROR,
      payload: {
        signatureId: embeddedData.signatureId,
        code,
      },
    },
    embeddedData.parentUrl,
  );
}

type AppConfigure = {
  type: MessageType.APP_CONFIGURE;
  payload: {
    allowCancel?: boolean;
  };
};

type SignerAppUserDeclineRequest = {
  type: MessageType.USER_DECLINE_REQUEST;
  payload: {
    signatureId: string;
    reason: string;
  };
};

type SignerAppUserSignRequest = {
  type: MessageType.USER_SIGN_REQUEST;
  payload: {
    signatureId: string;
  };
};

type MessagesWithData =
  | SignerAppUserDeclineRequest
  | SignerAppUserSignRequest
  | AppError
  | AppConfigure
  | ReassignRequest;

export type LookupMessageType<T = MessageType> =
  T extends MessagesWithData['type']
    ? Extract<MessagesWithData, { type: T }>
    : { type: T };

/**
 * useWindowMessage is a type-safe hook for recieving messages from
 * window.addEventListener('message', onMessage);
 *
 * When you specify the type, it looks up what data is expected in MessageType
 * and MessagesWithData.
 *
 * @AppExplorer https://miro.com/app/board/uXjVPXskloA=/?moveToWidget=3458764538186526183
 */
export function useWindowMessage<T extends MessageType>(
  type: T,
  callback: (m: LookupMessageType<T>, source: MessageEvent['source']) => void,
  {
    expectedOrigin = window.location.origin,
    expectedSource = window.parent,
  }: Options = {},
): null {
  const callbackRef = useLatestRef(callback);

  React.useEffect(() => {
    const origin = getExpectedOrigin(expectedOrigin);

    const onMessage = (event: MessageEvent) => {
      if (
        origin === event.origin &&
        event.source === expectedSource &&
        event.data?.type === type
      ) {
        callbackRef.current(event.data, event.source);
      }
    };

    window.addEventListener('message', onMessage);

    return () => window.removeEventListener('message', onMessage);
  }, [callbackRef, expectedOrigin, expectedSource, type]);
  return null;
}

export const canPostToParent = (
  parentUrl: string | null,
): parentUrl is string =>
  parentUrl != null &&
  // In situation such as P&S is opened in new a popup window prevent sending
  // message back to the window itself if it's same as parent
  window !== window.parent;

/**
 * postTo is a type-safe way to postMessage across iframes.
 *
 *
 * @AppExplorer https://miro.com/app/board/uXjVPXskloA=/?moveToWidget=3458764538186526166
 */
export function postTo<T extends MessageType>(
  targetWindow: Window,
  message: LookupMessageType<T>,
  // This just needs to postMessage to hellosign-web code through an iframe and
  // it needs to work in Storybook. I think the simplest solution is to just
  // always post to the same origin.
  origin = window.location.origin,
  transferrable?: Transferable[] | undefined,
) {
  targetWindow.postMessage(message, origin, transferrable);
}

type Options = {
  expectedOrigin?: string;
  expectedSource?: MessageEvent['source'];
};

function getExpectedOrigin(expectedOrigin: string): string | null {
  try {
    return new URL(expectedOrigin).origin;
  } catch {
    return null;
  }
}

/**
 * @enum {string}
 * @readonly
 */
export const messages = MessageType;

/**
 * Natively sends a cross-origin window message if the
 * user's navigator supports it, otherwise fails silently.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
 * @param {any} message
 * @param {string} targetOrigin
 * @param {Transferable} [transfer]
 * @returns {void}
 */
export function postMessage<T extends MessageType>(
  message: LookupMessageType<T>,
  targetOrigin: string,
  transferrable?: Transferable[] | undefined,
) {
  if (!targetOrigin) {
    return;
  }

  if (window.parent) {
    postTo(window.parent, message, targetOrigin, transferrable);
  }

  if (typeof Office !== 'undefined') {
    Office.onReady().then(() => {
      Office.context.ui.messageParent(JSON.stringify(message)); // NOSONAR
    });
  }
}
